import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import DefaultSEO from './components/seo/DefaultSEO';
import IOSInstallBanner from './components/pwa/IOSInstallBanner';
import ChunkWarmup from './components/perf/ChunkWarmup';
const InstallAppPage = lazy(() => import('./pages/InstallAppPage'));
const DiventaPartnerPage = lazy(() => import('./pages/DiventaPartnerPage'));
const PrenotaDemoPage = lazy(() => import('./pages/PrenotaDemoPage'));
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CookieBanner from './components/CookieBanner';
import SubscriptionBlockedModal from './components/SubscriptionBlockedModal';
import MobileBottomNav from './components/layout/MobileBottomNav';
import InstallPWAPrompt from './components/pwa/InstallPWAPrompt';
import { PushNotificationBanner } from './components/pwa/PushNotificationManager';

// Error Boundary to prevent runtime errors from showing to users
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('App error caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ fontSize: '18px', color: '#555', marginBottom: '20px' }}>Si è verificato un errore. Ricarica la pagina.</p>
          <button onClick={() => window.location.reload()} style={{ padding: '12px 24px', background: '#C8A96E', color: '#1a1a1a', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
            Ricarica
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Layout wrapper that conditionally shows header/footer
import AgencyLayout from './components/layout/AgencyLayout';

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const noLayoutPaths = ['/admin-login', '/admin-register'];
  const hideLayout = noLayoutPaths.includes(location.pathname);

  // Admin routes that use the admin layout (AdminLayout is embedded in AdminDashboardPage)
  const isAdminRoute = location.pathname.startsWith('/admin/');
  const isAdmin = isAuthenticated && user?.tipo === 'admin';

  // Agency routes that use the professional layout
  const agencyLayoutPaths = [
    '/dashboard-agenzia',
    '/agenzia/',
    '/necrologi/crea',
    '/pubblica-necrologio',
    '/area-privata',
  ];

  const isAgencyRoute = agencyLayoutPaths.some(p => location.pathname.startsWith(p) || location.pathname === p);
  const isAgency = isAuthenticated && (user?.role === 'agenzia' || user?.tipo === 'agenzia');

  if (hideLayout) {
    return <>{children}</>;
  }

  // Admin dashboard has its own layout
  if (isAdmin && isAdminRoute) {
    return (
      <>
        {children}
        <CookieBanner />
      </>
    );
  }

  if (isAgency && isAgencyRoute) {
    return (
      <>
        <AgencyLayout>{children}</AgencyLayout>
        <CookieBanner />
        <SubscriptionBlockedModal />
      </>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="flex-grow pb-[72px] md:pb-0">
        {children}
      </div>
      <Footer />
      <MobileBottomNav />
      <InstallPWAPrompt />
      <PushNotificationBanner />
      <CookieBanner />
      <SubscriptionBlockedModal />
    </div>
  );
};
import HomePage from './pages/HomePage';
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ObituaryDetailPage = lazy(() => import('./pages/ObituaryDetailPage'));
const EmailPreferencesPage = lazy(() => import('./pages/EmailPreferencesPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PrivateLoginPage = lazy(() => import('./pages/PrivateLoginPage'));
const AgencyLoginPage = lazy(() => import('./pages/AgencyLoginPage'));
const BloggerLoginPage = lazy(() => import('./pages/BloggerLoginPage'));
const BloggerRegisterPage = lazy(() => import('./pages/BloggerRegisterPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));
const PrivateAreaPage = lazy(() => import('./pages/PrivateAreaPage'));
const AgencyDashboardPage = lazy(() => import('./pages/AgencyDashboardPage'));
const BloggerDashboardPage = lazy(() => import('./pages/BloggerDashboardPage'));
const TestamentoDigitalePage = lazy(() => import('./pages/TestamentoDigitalePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogEreditaEticaPage = lazy(() => import('./pages/blog/BlogEreditaEticaPage'));
const BlogGlossarioSimboliFunerariPage = lazy(() => import('./pages/blog/BlogGlossarioSimboliFunerariPage'));
const BlogPensieroCommemorativoPage = lazy(() => import('./pages/blog/BlogPensieroCommemorativoPage'));
const BlogModuloCommentiPage = lazy(() => import('./pages/blog/BlogModuloCommentiPage'));
const BlogStoriaArteFunerariaPage = lazy(() => import('./pages/blog/BlogStoriaArteFunerariaPage'));
const BlogSetteFasiLuttoPage = lazy(() => import('./pages/blog/BlogSetteFasiLuttoPage'));
const BlogSupportoFuneraliOnlinePage = lazy(() => import('./pages/blog/BlogSupportoFuneraliOnlinePage'));
const BlogParlareBambiniLuttoPage = lazy(() => import('./pages/blog/BlogParlareBambiniLuttoPage'));
const BlogLuttoImprovvisoPage = lazy(() => import('./pages/blog/BlogLuttoImprovvisoPage'));
const BlogBurocraziaPostDecessoPage = lazy(() => import('./pages/blog/BlogBurocraziaPostDecessoPage'));
const BlogOrganizzareFuneralePage = lazy(() => import('./pages/blog/BlogOrganizzareFuneralePage'));
const BlogEreditaSuccessionePage = lazy(() => import('./pages/blog/BlogEreditaSuccessionePage'));
const BlogPrendersiCuraDiSePage = lazy(() => import('./pages/blog/BlogPrendersiCuraDiSePage'));
const BlogAnniversarioMortePage = lazy(() => import('./pages/blog/BlogAnniversarioMortePage'));
const BlogCommemorazioneDefuntiPage = lazy(() => import('./pages/blog/BlogCommemorazioneDefuntiPage'));
const BlogMessaSuffragioPage = lazy(() => import('./pages/blog/BlogMessaSuffragioPage'));
const BlogElaborazioneLuttoPage = lazy(() => import('./pages/blog/BlogElaborazioneLuttoPage'));
const BlogCimiteriMonumentaliPage = lazy(() => import('./pages/blog/BlogCimiteriMonumentaliPage'));
const BlogPostDetailPage = lazy(() => import('./pages/BlogPostDetailPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const AgencyListPage = lazy(() => import('./pages/AgencyListPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FamilyNoticesPage = lazy(() => import('./pages/FamilyNoticesPage'));
const RecentDeathsPage = lazy(() => import('./pages/RecentDeathsPage'));
const ServiceDirectoryPage = lazy(() => import('./pages/ServiceDirectoryPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const SupportoAlLuttoPage = lazy(() => import('./pages/SupportoAlLuttoPage'));
const ProfessionalRegistrationPage = lazy(() => import('./pages/ProfessionalRegistrationPage'));
const ProfessionalCategoryPage = lazy(() => import('./pages/ProfessionalCategoryPage'));
const CreditRechargePage = lazy(() => import('./pages/CreditRechargePage'));
const AssociationsPage = lazy(() => import('./pages/AssociationsPage'));
import PlaceholderPage from './pages/PlaceholderPage';
const CreateObituaryPage = lazy(() => import('./pages/CreateObituaryPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DonationSuccessPage = lazy(() => import('./pages/DonationSuccessPage'));
const MemorialCardPage = lazy(() => import('./pages/MemorialCardPage'));
const RicordinoPreviewPage = lazy(() => import('./pages/RicordinoPreviewPage'));
const ItinerariMemoriaPage = lazy(() => import('./pages/ItinerariMemoriaPage'));
const ItinerarioDetailPage = lazy(() => import('./pages/ItinerarioDetailPage'));
const LavoraConNoiPage = lazy(() => import('./pages/LavoraConNoiPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const RunProductionSeedPage = lazy(() => import('./pages/RunProductionSeedPage'));
const DocsPrivacyPage = lazy(() => import('./pages/DocsPrivacyPage'));
// SEO landing pages
const ComeScrivereNecrologioPage = lazy(() => import('./pages/seo/ComeScrivereNecrologioPage'));
const LuttoComeAffrontarloPage = lazy(() => import('./pages/seo/LuttoComeAffrontarloPage'));
const TestamentoDigitaleGuidaPage = lazy(() => import('./pages/seo/TestamentoDigitaleGuidaPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminRegisterPage = lazy(() => import('./pages/AdminRegisterPage'));
const ReportIncassiPage = lazy(() => import('./pages/ReportIncassiPage'));
const CandlePurchasePage = lazy(() => import('./pages/CandlePurchasePage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const CandleSuccessPage = lazy(() => import('./pages/CandleSuccessPage'));
const AgencyProfilePage = lazy(() => import('./pages/AgencyProfilePage'));
const AgencyPublicPage = lazy(() => import('./pages/AgencyPublicPage'));
const ObituaryPublishSuccessPage = lazy(() => import('./pages/ObituaryPublishSuccessPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AnniversariPage = lazy(() => import('./pages/AnniversariPage'));
const MesseSuffragioPage = lazy(() => import('./pages/MesseSuffragioPage'));
const PubblicaCommemorazionePage = lazy(() => import('./pages/PubblicaCommemorazionePage'));
const RichiediPubblicazionePage = lazy(() => import('./pages/RichiediPubblicazionePage'));
const RichiediPubblicazionePrivatoPage = lazy(() => import('./pages/RichiediPubblicazionePrivatoPage'));
const LeMieRichiestePage = lazy(() => import('./pages/LeMieRichiestePage'));
const PodcastPage = lazy(() => import('./pages/PodcastPage'));
const DocumentiLegaliPage = lazy(() => import('./pages/DocumentiLegaliPage'));
const CimiteriMapPage = lazy(() => import('./pages/CimiteriMapPage'));
const FloristMarketplacePage = lazy(() => import('./pages/FloristMarketplacePage'));
const FloristDetailPage = lazy(() => import('./pages/FloristDetailPage'));
const FloristRegistrationPage = lazy(() => import('./pages/FloristRegistrationPage'));
const FloristOrderCheckoutPage = lazy(() => import('./pages/FloristOrderCheckoutPage'));
const FloristOrderSuccessPage = lazy(() => import('./pages/FloristOrderSuccessPage'));
const AdvertisingTermsPage = lazy(() => import('./pages/AdvertisingTermsPage'));
const ChildSafetyStandardsPage = lazy(() => import('./pages/ChildSafetyStandardsPage'));
const ReportAbusePage = lazy(() => import('./pages/ReportAbusePage'));
const ContestSuspensionPage = lazy(() => import('./pages/ContestSuspensionPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const FloristLoginPage = lazy(() => import('./pages/FloristLoginPage'));
const FloristEmailVerificationPage = lazy(() => import('./pages/FloristEmailVerificationPage'));
const FloristPasswordResetPage = lazy(() => import('./pages/FloristPasswordResetPage'));
const VideoCondivisoPage = lazy(() => import('./pages/VideoCondivisoPage'));
const DigitalSuccessorPage = lazy(() => import('./pages/DigitalSuccessorPage'));
const FloristDashboardPage = lazy(() => import('./pages/FloristDashboardPage'));
const ProfessionalLoginPage = lazy(() => import('./pages/ProfessionalLoginPage'));
const ProfessionalDashboardPage = lazy(() => import('./pages/ProfessionalDashboardPage'));
const ProfessionalsPage = lazy(() => import('./pages/ProfessionalsPage'));
const MemorialPage = lazy(() => import('./pages/MemorialPage'));
const ManifestiRicordiniPage = lazy(() => import('./pages/ManifestiRicordiniPage'));
const LettereAlCieloPage = lazy(() => import('./pages/lettere/LettereAlCieloPage'));
const DocumentationMapPage = lazy(() => import('./pages/DocumentationMapPage'));
const FamilyAreaPage = lazy(() => import('./pages/FamilyAreaPage'));
const NecrologiCittaPage = lazy(() => import('./pages/NecrologiCittaPage'));
const APIDocumentationPage = lazy(() => import('./pages/APIDocumentationPage'));
const MemoryBookPage = lazy(() => import('./pages/MemoryBookPage'));
const LibroDellaMemoriaPage = lazy(() => import('./pages/LibroDellaMemoriaPage'));
const LibroMemoriaDemoPage = lazy(() => import('./pages/LibroMemoriaDemoPage'));
const ArchivioContrattiPage = lazy(() => import('./pages/ArchivioContrattiPage'));
const AgencyApiKeysPage = lazy(() => import('./pages/AgencyApiKeysPage'));
const AgencySettingsPage = lazy(() => import('./pages/AgencySettingsPage'));
const DelegaFamigliaPage = lazy(() => import('./pages/DelegaFamigliaPage'));
const FamilyOnboardingPage = lazy(() => import('./pages/FamilyOnboardingPage'));
const AdminRepairPage = lazy(() => import('./pages/AdminRepairPage'));
const StripeMonitorPage = lazy(() => import('./pages/StripeMonitorPage'));
const ProfessionalPublicPage = lazy(() => import('./pages/ProfessionalPublicPage'));
const MessaggioPrivatoPage = lazy(() => import('./pages/MessaggioPrivatoPage'));
import { Toaster } from 'sonner';

function App() {
  return (
    <ErrorBoundary>
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <DefaultSEO />
          <IOSInstallBanner />
          <ChunkWarmup />
          <Toaster position="top-center" richColors closeButton />
          <Layout>
            <Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <div style={{ width: 36, height: 36, border: '3px solid #e5e0d6', borderTop: '3px solid #c4a676', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
              </div>
            }>
            <Routes>
              {/* Homepage */}
              <Route path="/" element={<HomePage />} />

              {/* Installa app PWA */}
              <Route path="/app" element={<InstallAppPage />} />
              <Route path="/installa-app" element={<InstallAppPage />} />
              <Route path="/prenota-demo" element={<PrenotaDemoPage />} />
                
                {/* Itinerari della Memoria */}
                <Route path="/itinerari-memoria" element={<ItinerariMemoriaPage />} />
                
                {/* Necrologi */}
                <Route path="/necrologi" element={<NecrologiCittaPage />} />
                <Route path="/necrologi/:provincia" element={<NecrologiCittaPage />} />
                <Route path="/necrologi/:provincia/:slug" element={<ObituaryDetailPage />} />
                <Route path="/necrologio/:id" element={<ObituaryDetailPage />} />
                <Route path="/necrologio/:id/candela" element={<CandlePurchasePage />} />
                <Route path="/necrologio/:id/candela/successo" element={<CandleSuccessPage />} />
                <Route path="/memoriale/:id" element={<MemorialPage />} />
                <Route path="/necrologi/crea" element={<CreateObituaryPage />} />
                <Route path="/pubblica-necrologio" element={<CreateObituaryPage />} />
                <Route path="/pubblica-necrologio/successo" element={<ObituaryPublishSuccessPage />} />
                
                {/* Ricordino Preview (Public) */}
                <Route path="/ricordino/preview/:previewId" element={<RicordinoPreviewPage />} />
                
                {/* Donation Success Page */}
                <Route path="/donazione/successo" element={<DonationSuccessPage />} />
                
                {/* Authentication - Separate Login Pages */}
                <Route path="/login" element={<PrivateLoginPage />} />
                <Route path="/login-agenzia" element={<AgencyLoginPage />} />
                <Route path="/login-blogger" element={<BloggerLoginPage />} />
                <Route path="/registrazione-blogger" element={<BloggerRegisterPage />} />
                <Route path="/password-reset" element={<PasswordResetPage />} />
                <Route path="/verifica-email" element={<EmailVerificationPage />} />
                <Route path="/accesso" element={<LoginPage />} />
                
                {/* Lavora con noi */}
                <Route path="/lavora-con-noi" element={<LavoraConNoiPage />} />
                
                {/* Professional Directory */}
                <Route path="/professionisti" element={<ProfessionalsPage />} />
                <Route path="/supporto-al-lutto" element={<SupportoAlLuttoPage />} />
                <Route path="/professionisti/registrazione" element={<ProfessionalRegistrationPage />} />
                <Route path="/professionisti/categoria/:categoriaId" element={<ProfessionalCategoryPage />} />
                <Route path="/professionisti/:id" element={<ProfessionalPublicPage />} />
                
                {/* Credit Recharge - Requires Auth */}
                <Route 
                  path="/professionista/ricarica" 
                  element={
                      <CreditRechargePage />
                  } 
                />
                <Route 
                  path="/professionista/ricarica/successo" 
                  element={
                      <CreditRechargePage />
                  } 
                />
                
                {/* Admin Login/Register - No Header/Footer */}
                <Route path="/admin-login" element={<AdminLoginPage />} />
                <Route path="/admin-register" element={<AdminRegisterPage />} />
                
                {/* Documentation Map - Interactive */}
                <Route path="/documentazione" element={<DocumentationMapPage />} />
                <Route path="/libro-della-memoria" element={<LibroDellaMemoriaPage />} />
                <Route path="/libro-della-memoria/demo" element={<LibroMemoriaDemoPage />} />
                <Route path="/delega-famiglia/:necrologioId" element={<DelegaFamigliaPage />} />
                <Route path="/famiglia/onboarding/:necrologio_id" element={<FamilyOnboardingPage mode="refresh" />} />
                <Route path="/famiglia/onboarding/:necrologio_id/completato" element={<FamilyOnboardingPage mode="completato" />} />
                <Route path="/messaggio-privato/:token" element={<MessaggioPrivatoPage />} />
                
                {/* Admin Dashboard */}
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <ProtectedRoute requireType="admin">
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/admin/repair" 
                  element={
                    <ProtectedRoute requireType="admin">
                      <AdminRepairPage />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/admin/stripe-monitor" 
                  element={
                    <ProtectedRoute requireType="admin">
                      <StripeMonitorPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* Report Incassi - Super Admin */}
                <Route 
                  path="/admin/report-incassi" 
                  element={
                    <ProtectedRoute requireType="admin">
                      <ReportIncassiPage />
                    </ProtectedRoute>
                  }
                />

                {/* Production Seed Runner - Super Admin (pagina nascosta) */}
                <Route
                  path="/admin/run-seed"
                  element={
                    <ProtectedRoute requireType="admin">
                      <RunProductionSeedPage />
                    </ProtectedRoute>
                  }
                />

                {/* Docs Privacy Manager - Super Admin (pagina nascosta) */}
                <Route
                  path="/admin/privacy-docs"
                  element={
                    <ProtectedRoute requireType="admin">
                      <DocsPrivacyPage />
                    </ProtectedRoute>
                  }
                />

                {/* SEO Landing Pages (pubbliche, senza auth) */}
                <Route path="/guide/come-scrivere-necrologio" element={<ComeScrivereNecrologioPage />} />
                <Route path="/guide/lutto-come-affrontarlo" element={<LuttoComeAffrontarloPage />} />
                <Route path="/guide/testamento-digitale-cose" element={<TestamentoDigitaleGuidaPage />} />
                
                {/* Blogger Dashboard */}
                <Route path="/blogger/dashboard" element={<BloggerDashboardPage />} />
                
                {/* Subscription Page */}
                <Route path="/abbonamenti" element={<SubscriptionPage />} />
                
                {/* Protected Routes - Area Personale */}
                <Route 
                  path="/area-utente" 
                  element={
                  <ProtectedRoute requireType="privato">
                    <PrivateAreaPage />
                  </ProtectedRoute>
                } 
              />

              {/* Lascito Digitale - Public Page */}
              <Route path="/testamento-digitale" element={<TestamentoDigitalePage />} />
              
              {/* Protected Routes - Dashboard Agenzia */}
              <Route 
                path="/dashboard-agenzia" 
                element={
                  <ProtectedRoute requireType="agenzia">
                    <AgencyDashboardPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Ricordini Lutto - Agenzie */}
              <Route 
                path="/agenzia/ricordini" 
                element={
                  <ProtectedRoute requireType="agenzia">
                    <MemorialCardPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Manifesti e Ricordini - Agenzie */}
              <Route 
                path="/agenzia/manifesti-ricordini" 
                element={
                  <ProtectedRoute requireType="agenzia">
                    <ManifestiRicordiniPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Libro della Memoria - Agenzie */}
              <Route 
                path="/agenzia/libro-memoria" 
                element={
                  <ProtectedRoute requireType="agenzia">
                    <MemoryBookPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Agency Profile */}
              <Route 
                path="/agenzia/profilo" 
                element={
                  <ProtectedRoute requireType="agenzia">
                    <AgencyProfilePage />
                  </ProtectedRoute>
                } 
              />

              {/* Archivio Documenti Agenzia */}
              <Route 
                path="/agenzia/archivio-contratti" 
                element={
                  <ProtectedRoute requireType="agenzia">
                    <ArchivioContrattiPage />
                  </ProtectedRoute>
                } 
              />

              {/* Integrazione API - Agenzie */}
              <Route 
                path="/agenzia/api-keys" 
                element={
                  <ProtectedRoute requireType="agenzia">
                    <AgencyApiKeysPage />
                  </ProtectedRoute>
                } 
              />

              {/* Impostazioni Agenzia */}
              <Route
                path="/agenzia/impostazioni"
                element={
                  <ProtectedRoute requireType="agenzia">
                    <AgencySettingsPage />
                  </ProtectedRoute>
                }
              />
            
            {/* Agenzie Funebri */}
            <Route path="/agenzie" element={<AgencyListPage />} />
            <Route path="/onoranze-funebri/:id" element={<AgencyPublicPage />} />
            {/* SEO-friendly URL: /agenzia/{provincia}/{citta}/{slug} (must come BEFORE legacy :id) */}
            <Route path="/agenzia/:provincia_slug/:citta_slug/:slug" element={<AgencyPublicPage />} />
            <Route path="/agenzia/:id" element={<AgencyPublicPage />} />
            <Route path="/agenzie/:id" element={<AgencyPublicPage />} />

            {/* GDPR: public email-preferences page (HMAC-token gated, no login) */}
            <Route path="/email-preferences/:token" element={<EmailPreferencesPage />} />
            <Route 
              path="/partner" 
              element={<DiventaPartnerPage />} 
            />
            <Route path="/diventa-partner" element={<DiventaPartnerPage />} />
            
            {/* Shop Fiori */}
            <Route path="/fiori" element={<ShopPage />} />
            
            {/* Marketplace Fioristi */}
            <Route path="/fioristi" element={<FloristMarketplacePage />} />
            <Route path="/fiorista/:floristId" element={<FloristDetailPage />} />
            <Route path="/fiorista/registrazione" element={<FloristRegistrationPage />} />
            <Route path="/fiorista/login" element={<FloristLoginPage />} />
            <Route path="/fiorista/verifica-email" element={<FloristEmailVerificationPage />} />
            <Route path="/video-condiviso" element={<VideoCondivisoPage />} />
            <Route path="/cosa-succede-al-mio-account-dopo-la-morte" element={<DigitalSuccessorPage />} />
            <Route path="/successione-digitale" element={<DigitalSuccessorPage />} />
            <Route path="/fiorista/password-dimenticata" element={<FloristPasswordResetPage />} />
            <Route path="/fiorista/dashboard" element={<FloristDashboardPage />} />
            <Route path="/ordine-fiori/checkout" element={<FloristOrderCheckoutPage />} />
            <Route path="/ordine-fiori/successo" element={<FloristOrderSuccessPage />} />
            <Route path="/termini-pubblicita" element={<AdvertisingTermsPage />} />
            
            {/* Professional Dashboard */}
            <Route path="/professionista/login" element={<ProfessionalLoginPage />} />
            <Route path="/professionista/dashboard" element={<ProfessionalDashboardPage />} />
            
            {/* Anniversari, Trigesimi e Messe */}
            <Route path="/anniversari" element={<AnniversariPage />} />
            <Route path="/messe-suffraggio" element={<MesseSuffragioPage />} />
            <Route path="/pubblica-commemorazione" element={<PubblicaCommemorazionePage />} />
            <Route path="/richiedi-pubblicazione" element={<RichiediPubblicazionePage />} />
            <Route path="/richiedi-pubblicazione/privato" element={<RichiediPubblicazionePrivatoPage />} />
            <Route path="/le-mie-richieste" element={<LeMieRichiestePage />} />
            <Route path="/annunci-familiari" element={<FamilyNoticesPage />} />
            
            {/* Risorse e Guide */}
            <Route path="/risorse" element={<GuidePage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/podcast" element={<PodcastPage />} />
            <Route path="/cimiteri" element={<CimiteriMapPage />} />
            
            {/* Associazioni per Donazioni */}
            <Route path="/associazioni" element={<AssociationsPage />} />
            <Route path="/servizi" element={<ServiceDirectoryPage />} />
            <Route path="/directory-servizi" element={<ServiceDirectoryPage />} />
            
            {/* Lettere al Cielo */}
            <Route path="/lettere-al-cielo" element={<LettereAlCieloPage />} />
            
            {/* Area Famiglia Privata - Accesso con token */}
            <Route path="/famiglia/:token" element={<FamilyAreaPage />} />
            
            {/* Documentazione API per Gestionali */}
            <Route path="/docs/api" element={<APIDocumentationPage />} />
            <Route path="/partner/api" element={<APIDocumentationPage />} />
            <Route path="/integrazioni" element={<APIDocumentationPage />} />
            <Route path="/documentazione-api" element={<APIDocumentationPage />} />
            
            {/* Blog */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/eredita-etica" element={<BlogEreditaEticaPage />} />
            <Route path="/blog/glossario-simboli-funerari" element={<BlogGlossarioSimboliFunerariPage />} />
            <Route path="/blog/pensiero-commemorativo" element={<BlogPensieroCommemorativoPage />} />
            <Route path="/blog/modulo-commenti" element={<BlogModuloCommentiPage />} />
            <Route path="/blog/storia-arte-funeraria" element={<BlogStoriaArteFunerariaPage />} />
            <Route path="/blog/sette-fasi-lutto" element={<BlogSetteFasiLuttoPage />} />
            <Route path="/blog/supporto-funerali-online" element={<BlogSupportoFuneraliOnlinePage />} />
            <Route path="/blog/parlare-bambini-lutto" element={<BlogParlareBambiniLuttoPage />} />
            <Route path="/blog/lutto-improvviso" element={<BlogLuttoImprovvisoPage />} />
            <Route path="/blog/burocrazia-post-decesso" element={<BlogBurocraziaPostDecessoPage />} />
            <Route path="/blog/organizzare-funerale" element={<BlogOrganizzareFuneralePage />} />
            <Route path="/blog/eredita-successione" element={<BlogEreditaSuccessionePage />} />
            <Route path="/blog/prendersi-cura-di-se" element={<BlogPrendersiCuraDiSePage />} />
            <Route path="/blog/anniversario-di-morte-come-ricordare" element={<BlogAnniversarioMortePage />} />
            <Route path="/blog/commemorazione-defunti-tradizioni-italiane" element={<BlogCommemorazioneDefuntiPage />} />
            <Route path="/blog/messa-suffragio-guida-completa" element={<BlogMessaSuffragioPage />} />
            <Route path="/blog/elaborazione-del-lutto-fasi-come-superarlo" element={<BlogElaborazioneLuttoPage />} />
            <Route path="/blog/cimiteri-monumentali-italia-piu-belli" element={<BlogCimiteriMonumentaliPage />} />
            <Route 
              path="/blog/:id" 
              element={<BlogPostDetailPage />}
            />

            {/* Itinerari della Memoria - Dettaglio */}
            <Route path="/itinerari/:slug" element={<ItinerarioDetailPage />} />

            <Route path="/faq" element={<FAQPage />} />
            
            {/* Informazioni */}
            <Route path="/chi-siamo" element={<AboutPage />} />
            <Route path="/contatti" element={<ContactPage />} />
            
            {/* Legal */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/termini" element={<TermsPage />} />
            <Route path="/termini-condizioni" element={<TermsPage />} />
            <Route path="/termini-e-condizioni" element={<TermsPage />} />
            <Route path="/cookie" element={<CookiePolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/documenti-legali" element={<DocumentiLegaliPage />} />
            <Route path="/documenti" element={<DocumentiLegaliPage />} />
            <Route path="/child-safety-standards" element={<ChildSafetyStandardsPage />} />
            <Route path="/child-safety" element={<ChildSafetyStandardsPage />} />
            <Route path="/segnala-abuso" element={<ReportAbusePage />} />
            <Route path="/report-abuse" element={<ReportAbusePage />} />
            <Route path="/contesta-sospensione" element={<ContestSuspensionPage />} />
            <Route path="/biblioteca-documenti" element={<ResourcesPage />} />
            <Route path="/download" element={<ResourcesPage />} />
            <Route path="/risorse-documenti" element={<ResourcesPage />} />
            
            {/* 404 */}
            <Route 
              path="*" 
              element={
                <PlaceholderPage 
                  title="Pagina Non Trovata" 
                  description="La pagina che stai cercando non esiste"
                />
              } 
            />
          </Routes>
          </Suspense>
          </Layout>
        </Router>
      </AuthProvider>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
